import { Box, Card, Typography, Stack, Button, Chip, alpha, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Iconify from '../../components/iconify';
import { AgentRequestPayload } from '../../hooks/useVoiceAgentSocket';

interface VoiceAgentRequestQueueProps {
  requests: AgentRequestPayload[];
  onTake: (orderId: number) => void;
  isLoading?: boolean;
}

function formatElapsed(iso?: string) {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(diffMs / 1000);
  if (secs < 60) return `il y a ${secs}s`;
  return `il y a ${Math.floor(secs / 60)}min`;
}

function formatTime(iso?: string) {
  if (!iso) return '--:--';
  return new Intl.DateTimeFormat('fr-CA', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

export default function VoiceAgentRequestQueue({ requests, onTake, isLoading }: VoiceAgentRequestQueueProps) {
  const theme = useTheme();
  const pending = requests.filter(r => r.type === 'NEW');

  if (pending.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 10,
          gap: 1.5,
          borderRadius: 3,
          border: `1px dashed ${alpha(theme.palette.divider, 0.2)}`,
          bgcolor: alpha(theme.palette.grey[500], 0.02),
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.grey[500], 0.08),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Iconify icon="mdi:headset-off" width={26} sx={{ color: 'text.disabled' }} />
        </Box>
        <Typography variant="body2" color="text.disabled" fontWeight={500}>
          Aucune demande en attente
        </Typography>
        <Typography variant="caption" color="text.disabled">
          Les demandes apparaissent ici en temps réel
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1.5}>
      {pending.map((req, index) => (
        <Card
          key={req.orderId}
          sx={{
            overflow: 'hidden',
            borderRadius: 2.5,
            border: `1.5px solid ${alpha(theme.palette.warning.main, 0.35)}`,
            boxShadow: `0 2px 12px ${alpha(theme.palette.warning.main, 0.08)}`,
            animation: `slideIn 0.25s ease ${index * 60}ms both`,
            '@keyframes slideIn': {
              from: { opacity: 0, transform: 'translateY(-10px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          {/* Accent top bar */}
          <Box
            sx={{
              height: 3,
              bgcolor: 'warning.main',
              animation: 'shimmer 2s ease-in-out infinite',
              backgroundImage: `linear-gradient(90deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.light} 50%, ${theme.palette.warning.main} 100%)`,
              backgroundSize: '200% 100%',
              '@keyframes shimmer': {
                '0%': { backgroundPosition: '200% 0' },
                '100%': { backgroundPosition: '-200% 0' },
              },
            }}
          />

          <Box sx={{ p: 2 }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
              {/* Left: info */}
              <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                  <Chip
                    icon={<Iconify icon="mdi:account-voice" width={13} />}
                    label={`Commande #${req.orderId}`}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      height: 24,
                      bgcolor: alpha(theme.palette.warning.main, 0.12),
                      color: theme.palette.warning.dark,
                      '& .MuiChip-icon': { color: theme.palette.warning.dark },
                    }}
                  />
                  <Typography variant="caption" color="text.disabled" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatTime(req.requestedAt)} · {formatElapsed(req.requestedAt)}
                  </Typography>
                </Stack>

                {req.transcript ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontStyle: 'italic',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: 1.5,
                    }}
                  >
                    &ldquo;{req.transcript}&rdquo;
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.disabled">
                    Aucun transcript disponible
                  </Typography>
                )}
              </Stack>

              {/* Right: action */}
              <Button
                variant="contained"
                color="warning"
                size="small"
                disabled={isLoading}
                onClick={() => onTake(req.orderId)}
                startIcon={<Iconify icon="mdi:phone-in-talk" width={16} />}
                sx={{
                  fontWeight: 700,
                  borderRadius: 1.5,
                  minWidth: 130,
                  flexShrink: 0,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.warning.main, 0.35)}`,
                  '&:hover': {
                    boxShadow: `0 4px 14px ${alpha(theme.palette.warning.main, 0.45)}`,
                  },
                }}
              >
                Prendre en charge
              </Button>
            </Stack>
          </Box>
        </Card>
      ))}
    </Stack>
  );
}
